import React, { useEffect, useMemo, useRef, useState } from "react";
import * as api from "../../api";

const BTN =
  "inline-flex items-center gap-1 rounded-lg border border-[#1f2638] bg-[#121825] px-2.5 py-1.5 text-xs hover:bg-[#141b2d] focus:outline-none focus:ring-2 focus:ring-violet-600/40 disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_PRIMARY =
  "inline-flex items-center gap-1 rounded-lg border border-violet-600/40 bg-violet-600/20 px-2.5 py-1.5 text-xs hover:bg-violet-600/30 focus:ring-2 focus:ring-violet-600/60 disabled:opacity-60 disabled:cursor-not-allowed";

const INP =
  "w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40 text-slate-200 placeholder:text-slate-500";

const PREFIX = "+359";

function enforceBgPrefix(raw) {
  let s = String(raw || "").trim();
  s = s.replace(/[()\s-]/g, "");
  if (s.startsWith("00")) s = "+" + s.slice(2);
  s = s.replace(/[^\d+]/g, "");
  if (!s.startsWith(PREFIX)) {
    s = s.replace(/^\+/, "");
    if (s.startsWith("359")) s = s.slice(3);
    s = PREFIX + s;
  }

  if (s.length < PREFIX.length) return PREFIX;
  return PREFIX + s.slice(PREFIX.length).replace(/\D/g, "");
}

export default function RealCall({ onBack }) {
  const inputRef = useRef(null);
  const [phone, setPhone] = useState(PREFIX);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const phoneNorm = useMemo(() => enforceBgPrefix(phone), [phone]);

  const canCall = useMemo(() => {
    const tailDigits = phoneNorm.slice(PREFIX.length).replace(/\D/g, "");
    return tailDigits.length >= 6;
  }, [phoneNorm]);

  const submit = async () => {
    if (!canCall || busy) return;

    setBusy(true);
    try {
      await api.convaiOutbound(phoneNorm);
      setToast({ type: "ok", text: "Заявката за обаждане е изпратена." });
    } catch (e) {
      setToast({ type: "err", text: e?.message || "Грешка при обаждане." });
    } finally {
      setBusy(false);
    }
  };

  const keepCaretOutOfPrefix = () => {
    const el = inputRef.current;
    if (!el) return;
    const pos = el.selectionStart ?? PREFIX.length;
    if (pos < PREFIX.length) {
      el.setSelectionRange(PREFIX.length, PREFIX.length);
    }
  };

  return (
    <div className="card rounded-2xl p-6">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg font-bold">Реално обаждане</h2>

        <div className="ml-auto flex items-center gap-2">
          {onBack && (
            <button className={BTN} onClick={onBack} disabled={busy}>
              Назад
            </button>
          )}
        </div>
      </div>

      <div className="mt-5 max-w-md">
        <label className="text-sm text-slate-200">Телефон</label>

        <input
          ref={inputRef}
          className={INP}
          value={phone}
          onChange={(e) => {
            const next = enforceBgPrefix(e.target.value);
            setPhone(next);
            requestAnimationFrame(keepCaretOutOfPrefix);
          }}
          onFocus={() => {
            requestAnimationFrame(() => {
              const el = inputRef.current;
              if (!el) return;
              el.setSelectionRange(PREFIX.length, PREFIX.length);
            });
          }}
          onClick={() => requestAnimationFrame(keepCaretOutOfPrefix)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              submit();
              return;
            }

            const el = inputRef.current;
            if (!el) return;

            const start = el.selectionStart ?? 0;
            const end = el.selectionEnd ?? 0;
            const touchesPrefix = start < PREFIX.length || end < PREFIX.length;

            if (touchesPrefix) {
              const allowed =
                e.key === "ArrowLeft" ||
                e.key === "ArrowRight" ||
                e.key === "ArrowUp" ||
                e.key === "ArrowDown" ||
                e.key === "Tab" ||
                e.key === "Home" ||
                e.key === "End" ||
                (e.ctrlKey || e.metaKey);

              if (!allowed && (e.key === "Backspace" || e.key === "Delete")) {
                e.preventDefault();
                requestAnimationFrame(keepCaretOutOfPrefix);
              }
            } else {
              if (e.key === "Backspace" && start === PREFIX.length && start === end) {
                e.preventDefault();
                requestAnimationFrame(keepCaretOutOfPrefix);
              }
            }
          }}
          inputMode="tel"
          autoComplete="tel"
          disabled={busy}
        />

        <button
          className={`${BTN_PRIMARY} mt-3 w-auto min-w-[150px] justify-center`}
          onClick={submit}
          disabled={!canCall || busy}
          title={!canCall ? "Въведи валиден номер" : "Обади се"}
        >
          {busy ? "Обаждане…" : "Обади се"}
        </button>
      </div>

      {toast && (
        <div
          className={[
            "fixed bottom-5 right-5 z-50 rounded-xl border px-3 py-2 text-sm shadow-lg",
            toast.type === "ok"
              ? "bg-emerald-900/30 border-emerald-700/40 text-emerald-200"
              : "bg-rose-900/30 border-rose-700/40 text-rose-200",
          ].join(" ")}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
